import { useGLTF, Text3D, Float, Center } from "@react-three/drei"
import { useRef } from "react";
import Star from "../meshes/Stars/Star"
import Ufo from "../meshes/Ufo"
import Rocket from "../meshes/Rocket"
import { useFrame } from "@react-three/fiber";
import YootMesh from "../meshes/YootMesh";
import { animated, useSpring } from "@react-spring/three";
import { languageAtom, turnAtom } from "../GlobalState";
import { useAtom } from "jotai";
import translations from "../dictionaries/translations";

export default function Catch3UfoAlert({ position, rotation, bonus=true }) {
  const [language] = useAtom(languageAtom);
  const fontUrl = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json";

  const borderMesh0Ref = useRef();
  const borderMesh1Ref = useRef();
  const borderMesh2Ref = useRef();
  const borderMesh3Ref = useRef();
  const borderMesh4Ref = useRef();
  const borderMesh5Ref = useRef();
  const borderMesh6Ref = useRef();
  const borderMeshRefs = [
    borderMesh0Ref,
    borderMesh1Ref,
    borderMesh2Ref,
    borderMesh3Ref,
    borderMesh4Ref,
    borderMesh5Ref,
    borderMesh6Ref
  ]

  const height = 2.2
  const width = 2.9
  useFrame((state, delta) => {
    for (let i = 0; i < borderMeshRefs.length; i++) {      
      borderMeshRefs[i].current.position.x = Math.cos(state.clock.elapsedTime / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
      borderMeshRefs[i].current.position.y = 0.1
      borderMeshRefs[i].current.position.z = Math.sin(state.clock.elapsedTime / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
    }
  })

  function YootEmoji() {
    return <group name='yoot-emoji'>
      <YootMesh position={[-0.15, 0, 0]} rotation={[0, Math.PI/2, -Math.PI/2]} scale={0.05}/>
      <YootMesh position={[-0.05, 0, 0]} rotation={[0, Math.PI/2, -Math.PI/2]} scale={0.05}/>
      <YootMesh position={[0.05, 0, 0]} rotation={[0, Math.PI/2, -Math.PI/2]} scale={0.05}/>
      <YootMesh position={[0.15, 0, 0]} rotation={[0, Math.PI/2, -Math.PI/2]} scale={0.05}/>
    </group>
  }

  function BamImage({position, rotation, scale, color}) {
    const { nodes, materials } = useGLTF('/models/bam-emoji.glb')
    return (
      <group position={position} rotation={rotation} scale={scale} dispose={null}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Plane.geometry}
          material={nodes.Plane.material}
          position={[0, 0, 0]}
          scale={0.168}
        >
          <meshStandardMaterial color={color}/>
        </mesh>
      </group>
    )
  }

  function handleAlertClick(e) {
    e.stopPropagation();
  }

  function RocketCatchUfo({ position, rotation, scale }) {
    const ufoGroupRef0 = useRef()
    const ufoGroupRef1 = useRef()
    const ufoGroupRef2 = useRef()
    const ufoGroupRefs = [ufoGroupRef0, ufoGroupRef1, ufoGroupRef2]
    const rocketRef = useRef()
    useFrame((state, delta) => {
      for (let i = 0; i < ufoGroupRefs.length; i++) {
        ufoGroupRefs[i].current.rotation.x = state.clock.elapsedTime + i * 0.7
        ufoGroupRefs[i].current.rotation.y = state.clock.elapsedTime + i * 1.4
        ufoGroupRefs[i].current.rotation.z = state.clock.elapsedTime + i * 2.1
      }
    })
    return <group name='catch-picture' position={position} rotation={rotation} scale={scale}>
      <group ref={rocketRef} rotation={[0, 0, 0]}>
        <Rocket
          position={[-1.2, 0.6, -0.9]} 
          scale={1.3}
          onBoard
        />
      </group>
      <mesh position={[-0.2, 0.4, -0.9]}>
        <sphereGeometry args={[0.05, 32, 16]}/>
        <meshStandardMaterial color='red'/>
      </mesh>
      <mesh position={[0.2, 0.4, -0.9]}>
        <sphereGeometry args={[0.05, 32, 16]}/>
        <meshStandardMaterial color='red'/>
      </mesh>
      <group ref={ufoGroupRef0} position={[0.8, 1, -1]}>
        <Ufo 
          rotation={[Math.PI/2, -Math.PI/8 * 5, Math.PI]} 
          scale={0.7}
          glassOpacity={0.1}
        />
      </group>
      <group ref={ufoGroupRef1} position={[1.4, 1, -0.8]}>
        <Ufo 
          rotation={[Math.PI/2, -Math.PI/8 * 5, Math.PI]} 
          scale={0.7}
          glassOpacity={0.1}
        />
      </group>
      <group ref={ufoGroupRef2} position={[0.8, 1, -0.4]}>
        <Ufo 
          rotation={[Math.PI/2, -Math.PI/8 * 5, Math.PI]} 
          scale={0.7}
          glassOpacity={0.1}
        />
      </group>
      <group position={[1, 0.3, -0.8]} scale={1.4}>
        <BamImage position={[0, 0, 0]} rotation={[0, -Math.PI, 0]} scale={[1, 2, 1]} color='#E73D3D'/>
        <BamImage position={[0, 0, 0]} rotation={[0, -Math.PI, 0]} scale={[0.8, 3, 0.8]} color='orange'/>
        <BamImage position={[0, 0, 0]} rotation={[0, -Math.PI, 0]} scale={[0.6, 4, 0.6]} color='yellow'/>
      </group>
    </group>
  }

  return <animated.group position={position} rotation={rotation} onPointerDown={(e) => handleAlertClick(e)}>
    <mesh
      castShadow
      receiveShadow
      scale={[width, 0.055, height]}
    >
      <cylinderGeometry args={[1, 1, 1, 64]}/>
      <meshStandardMaterial color='black' opacity={0.7} transparent/>
    </mesh>
    <RocketCatchUfo position={[0, 0, 0]} rotation={[0, 0, 0]} scale={[1, 1, 1]}/>
    <Center position={[0, 0.3, 0.4]} key={Math.random()}>
      <Text3D
        font={fontUrl}
        rotation={[Math.PI/2, Math.PI, Math.PI]}
        size={0.8}
        height={0.01}
      >
        {translations.alerts.catch[language]}
        <meshStandardMaterial color='red'/>
      </Text3D>
    </Center>
    { bonus && <Center position={[0, 0.3, 1.3]} key={Math.random()}>
      <Text3D
        font={fontUrl}
        rotation={[Math.PI/2, Math.PI, Math.PI]}
        size={0.4}
        height={0.01}
      >
        {translations.alerts.bonusThrow[language]}  
        <meshStandardMaterial color='red'/>
      </Text3D>
    </Center> }
    <group ref={borderMesh0Ref}>
      <YootEmoji/>
    </group>
    <group ref={borderMesh1Ref}>
      <Star scale={0.2} color='red'/>
    </group>
    <group ref={borderMesh2Ref}>
      <Star scale={0.2} color='red'/>
    </group>
    <group ref={borderMesh3Ref}>
      <Star scale={0.2} color='red'/>
    </group>
    <group ref={borderMesh4Ref}>
      <Star scale={0.2} color='red'/>
    </group>
    <group ref={borderMesh5Ref}>
      <Star scale={0.2} color='red'/>
    </group>
    <group ref={borderMesh6Ref}>
      <Star scale={0.2} color='red'/>
    </group>
  </animated.group>
}